import React, { FC, useEffect } from "react";

import clsx from "clsx";
import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { TruckIcon } from "@/assets/icons";
import { ActivitiesPanel } from "@/components/features/Activities";
import { StandardLayout } from "@/components/layouts";
import { InfoBox } from "@/components/molecules";
import { DetailsPage } from "@/components/templates";
import { StyledActionItem } from "@/components/templates/DetailsPage/styled";
import { getTodaysDate } from "@/core/date";
import mobileIntegration from "@/integration";
import { requestApi } from "@/shared/apis";
import { Permissions } from "@/shared/domain";
import { isABroker, useAuth, useBreadcrumb, useNavigation } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import PolicyholderCard from "./components/PolicyholderCard/PolicyholderCard";
import PolicyholderContentPanels from "./components/PolicyholderContentPanels/PolicyholderContentPanels";
import {
    AdministrationFormType,
    ChangeAddressFormType,
    CommercialFormType,
    ContactFormType,
    DriverLicenseAndIdFormType,
    FinanceCompanyFormType,
    FinanceFormType,
    GeneralCompanyDetailsFormType,
    GeneralDetailsFormType,
    ProfessionFormType,
} from "./formType";
import { getFormDetailByArea, mapPolicyholderRequest, PersonalDetailsAreas } from "./utils";

const PolicyholderDetailPage: FC = () => {
    const queryClient = useQueryClient();
    const { incUsageCount } = useUserFeedback();
    const { navToRelatedActivities, navToRelatedDocuments, navToContextualConversation, navToAddressChange } =
        useNavigation();
    const { t } = useTranslation();
    const { setPageTitle } = useBreadcrumb();
    const { id } = useParams();
    const policyholderId = Number(id);
    const { user } = useAuth();
    const ibpAclId = user.getAccessRightFor(policyholderId).accessControlListId;

    const allowedToSeeActivities = user.hasPermission(Permissions.ViewPolicyholderActivities, policyholderId);
    const allowedToAccessConversations = user.hasPermission(Permissions.AccessConversations, policyholderId);
    const allowedToSeeLinkedDocuments = user.hasPermission(Permissions.ViewLinkedDocuments, policyholderId);
    // Personal detail area
    const { data: personalDetails } = useQuery(
        ["personalDetails", { policyholderId }],
        () => requestApi.getPersonalDetails(policyholderId, ibpAclId),
        {
            select: mapPolicyholderRequest,
        },
    );

    useEffect(() => {
        if (personalDetails) {
            setPageTitle(personalDetails.currentDetails.fullName);
        }
    }, [personalDetails]);

    const { currentDetails, pendingDetails, isPhysicalPerson } = personalDetails || {};

    const { mutate: updatePersonalDetails, isLoading: isLoadingUpdatePersonalDetails } = useMutation(
        requestApi.updatePersonalDetails,
        {
            onSuccess: () => {
                queryClient.invalidateQueries("requestsPaginated");
                queryClient.invalidateQueries("personalDetails");
            },
        },
    );

    const handleSubmitPersonalDetails = (
        formValue:
            | GeneralDetailsFormType
            | GeneralCompanyDetailsFormType
            | DriverLicenseAndIdFormType
            | FinanceFormType
            | CommercialFormType
            | ProfessionFormType
            | AdministrationFormType
            | FinanceCompanyFormType
            | ContactFormType,
        detailArea: PersonalDetailsAreas,
    ) => {
        const values = {
            ...(pendingDetails || currentDetails),
            ...formValue,
        };

        const tagManagerProps = getFormDetailByArea(detailArea);
        try {
            updatePersonalDetails({
                body: values,
                policyholderId,
                params: { ibpAclId },
            });
            tagManager.pushEditPersonalDetailsSentEvent(tagManagerProps.id, tagManagerProps.name);
        } catch (e) {
            tagManager.pushEditPersonalDetailsSentEvent(tagManagerProps.id, tagManagerProps.name, false);
            throw e;
        }
    };

    // Relations area
    const { data: relations } = useQuery(
        ["relations", { policyholderId }],
        () => requestApi.getRelations(policyholderId),
        {
            enabled: !!isPhysicalPerson,
        },
    );

    const { currentRelations = [] } = relations || {};

    // Address area

    const { data: addressDetails } = useQuery(["address", { policyholderId }], () =>
        requestApi.getAddress(policyholderId, ibpAclId),
    );

    const { mutate: requestAddressChange, isLoading: isLoadingRequestAddressChange } = useMutation(
        requestApi.requestAddressChange,
        {
            onSuccess: () => {
                queryClient.invalidateQueries("requestsPaginated");
                queryClient.invalidateQueries("address");
            },
        },
    );

    const handleSubmitAddressDetails = (formValue: ChangeAddressFormType) => {
        const selectedFamilyMembers = currentRelations.filter((relation) =>
            (formValue.concerning || []).includes(relation.id),
        );
        const values: MyBrokerApi.AddressChangeRequest = {
            currentAddress: {
                addressDetail: addressDetails.currentAddress.addressDetail,
            },
            pendingAddress: {
                addressDetail: {
                    ...formValue,
                    country: { id: formValue.country.id },
                    activationDate: getTodaysDate(),
                },
                familyMembers: selectedFamilyMembers,
            },
        };
        requestAddressChange({
            policyholderId,
            body: values,
            params: {
                ibpAclId,
            },
        });
    };

    // End area

    useMount(() => {
        log.info("Policyholder loaded", personalDetails);
        incUsageCount();
        tagManager.pushViewPersonalDetailsEvent();
    });

    const pendingInfo = !!relations?.pendingRelations.length || pendingDetails || addressDetails?.pendingAddress;

    const handleShowMoreActivities = () => {
        navToRelatedActivities(policyholderId, currentDetails?.fullName, "policyholder", policyholderId, ibpAclId);
    };

    const handleShowDocuments = () => {
        navToRelatedDocuments(policyholderId, currentDetails?.fullName, "policyholders");
    };

    const handleAccessConversations = () => {
        navToContextualConversation(`${policyholderId}`, "party", currentDetails?.fullName, policyholderId);
    };

    const extraButtons = () => {
        const buttons = [];
        if (!mobileIntegration.isNativeApp() && user.hasPermission(Permissions.AccessChangeAddress, policyholderId)) {
            buttons.push(
                <span key="actionItem.AddressChange" title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}>
                    <StyledActionItem
                        className={clsx(isABroker() && "disabled")}
                        onClick={() => navToAddressChange(policyholderId)}
                        $disabled={isABroker()}
                        startIcon={<TruckIcon />}
                        data-testid="policyholder_detail_page-button-change_address"
                    >
                        {t("AccountUser.ChangeAddress")}
                    </StyledActionItem>
                </span>,
            );
        }
        return buttons;
    };

    return (
        <StandardLayout title={t("PageTitles.PersonalDetails")} disableSearch>
            {pendingInfo && <InfoBox type="success">{t("User.PersonalDetails.PendingInfo")}</InfoBox>}
            <DetailsPage
                objectType="policyholder"
                handleShowDocuments={allowedToSeeLinkedDocuments && handleShowDocuments}
                handleShowConversation={allowedToAccessConversations && handleAccessConversations}
                extraButtons={extraButtons()}
                activities={
                    allowedToSeeActivities && (
                        <ActivitiesPanel
                            policyholderId={policyholderId}
                            featureType="policyholder"
                            onShowMoreClick={handleShowMoreActivities}
                        />
                    )
                }
                content={
                    <PolicyholderContentPanels
                        personalDetails={personalDetails}
                        onSubmitPersonalDetails={handleSubmitPersonalDetails}
                        isLoadingSubmit={isLoadingUpdatePersonalDetails || isLoadingRequestAddressChange}
                        addressDetails={addressDetails}
                        onSubmitAddressDetails={handleSubmitAddressDetails}
                        relations={relations}
                    />
                }
                card={
                    <PolicyholderCard
                        policyholderId={policyholderId}
                        personalDetails={personalDetails}
                        onSubmitPersonalDetails={handleSubmitPersonalDetails}
                        isLoadingSubmit={isLoadingUpdatePersonalDetails}
                    />
                }
            />
        </StandardLayout>
    );
};

export default PolicyholderDetailPage;
